import { DRP } from './drp';

/** Campos numéricos do D.R.P — os únicos que as tabelas renderizam */
export type CampoNumericoDRP = { [K in keyof DRP]: DRP[K] extends number ? K : never }[keyof DRP];

/** Se a linha é uma etapa da cascata ou um dos componentes que a formam */
export type NivelLinhaDRP = 'cascata' | 'componente';

/** Qual permissão a linha exige para ser exibida */
export type PermissaoLinhaDRP = 'sempre' | 'custos' | 'drp';

/**
 * Uma linha do D.R.P.
 *
 * O resumo do pedido exibe as linhas na vertical, com `campoParticipacao` e `campoComposicao` em
 * colunas próprias; a tabela de itens exibe cada linha como um grupo de colunas na horizontal, com
 * `campoPerc` — a alíquota aplicada. É a mesma cascata lida de dois jeitos, por isso uma lista só.
 */
export interface LinhaDRP {
    /** Prefixo da etapa no resumo do pedido, ex. `5.1. ( - )`. Só nas linhas da cascata */
    numero?: string;
    /** Rótulo no resumo do pedido */
    titulo: string;
    /** Rótulo no cabeçalho da tabela de itens, quando o do resumo não cabe na coluna */
    tituloItem?: string;
    /** Rótulo completo na legenda, quando o título vem abreviado ou a base do percentual não é óbvia */
    legenda?: string;
    /** Sufixo das classes css da tabela de itens: col-drp-valor-<slug> e col-drp-perc-<slug> */
    slug: string;
    campoValor: CampoNumericoDRP;
    /** Percentual da tabela de itens: a alíquota aplicada */
    campoPerc?: CampoNumericoDRP;
    /** Participação sobre o valor total líquido. Ausente nas linhas anteriores ao líquido */
    campoParticipacao?: CampoNumericoDRP;
    /** Composição sobre o valor total bruto */
    campoComposicao?: CampoNumericoDRP;
    /** Percentual que vale por definição, sem campo no D.R.P (100% do líquido na linha do líquido) */
    participacaoFixa?: number;
    /** Idem para a composição (100% do bruto na linha do bruto) */
    composicaoFixa?: number;
    nivel: NivelLinhaDRP;
    permissao: PermissaoLinhaDRP;
}

const SOBRE_BRUTO = '% sobre o valor bruto';
const SOBRE_LIQUIDO = '% sobre o valor líquido';

/**
 * As linhas do D.R.P na ordem da cascata, com os componentes logo depois da etapa que eles formam.
 *
 * Essa ordem serve as duas leituras: no resumo os componentes aparecem recuados sob a sua etapa; na
 * tabela de itens a etapa dá lugar a eles, e a sequência que sobra continua sendo a da cascata.
 */
export const LINHAS_DRP: LinhaDRP[] = [
    {
        numero: '1.', titulo: 'TOTAL BRUTO', tituloItem: 'Total Bruto', slug: 'total-bruto',
        legenda: 'Valor total bruto do item', campoValor: 'valorTotalBruto', composicaoFixa: 100,
        nivel: 'cascata', permissao: 'sempre'
    },

    {
        numero: '2. ( - )', titulo: "DVV's", tituloItem: "( - ) DVV's", slug: 'dvvs',
        legenda: "DVV's — ICMS, PIS, COFINS, frete, perda e devolução, custo financeiro, desconto em contrato, royalties, IPI e comissão. " + SOBRE_BRUTO,
        campoValor: 'valorDvvs', campoPerc: 'percDvvs', campoComposicao: 'percDvvsComposicao',
        nivel: 'cascata', permissao: 'custos'
    },
    {
        titulo: 'ICMS', slug: 'icms', legenda: 'ICMS — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorIcms', campoPerc: 'percAliquotaIcms', campoComposicao: 'percIcmsComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'PIS', slug: 'pis', legenda: 'PIS — a alíquota incide sobre o valor bruto menos o ICMS, então difere da composição',
        campoValor: 'valorPis', campoPerc: 'percAliquotaPis', campoComposicao: 'percPisComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'COFINS', slug: 'cofins', legenda: 'COFINS — a alíquota incide sobre o valor bruto menos o ICMS, então difere da composição',
        campoValor: 'valorCofins', campoPerc: 'percAliquotaCofins', campoComposicao: 'percCofinsComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Frete', slug: 'frete', legenda: 'Frete — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorDvvFrete', campoPerc: 'percDvvFrete', campoComposicao: 'percDvvFreteComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Perda e Devolução', tituloItem: 'Perda e Devol.', slug: 'perda-devolucao',
        legenda: 'Perda e Devolução — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorPerdaDevolucao', campoPerc: 'percPerdaDevolucao', campoComposicao: 'percPerdaDevolucaoComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Custo Financeiro', tituloItem: 'Custo Financ.', slug: 'custo-financeiro',
        legenda: 'Custo Financeiro — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorCustoFinanceiro', campoPerc: 'percCustoFinanceiro', campoComposicao: 'percCustoFinanceiroComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Desconto em Contrato', tituloItem: 'Desc. Contrato', slug: 'desconto-contrato',
        legenda: 'Desconto em Contrato — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorDescontoContrato', campoPerc: 'percDescontoContrato', campoComposicao: 'percDescontoContratoComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Royalties', slug: 'royalties', legenda: 'Royalties — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorRoyalties', campoPerc: 'percRoyalties', campoComposicao: 'percRoyaltiesComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'IPI', slug: 'ipi', legenda: 'IPI — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorIpi', campoPerc: 'percAliquotaIpi', campoComposicao: 'percIpiComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Comissão', slug: 'comissao', legenda: 'Comissão — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorComissao', campoPerc: 'percComissao', campoComposicao: 'percComissaoComposicao',
        nivel: 'componente', permissao: 'custos'
    },

    {
        numero: '3. ( = )', titulo: 'TOTAL LÍQUIDO', tituloItem: '( = ) Total Líquido', slug: 'total-liquido',
        legenda: "Total Líquido — valor bruto menos as DVV's",
        campoValor: 'valorTotalLiquido', participacaoFixa: 100, campoComposicao: 'percTotalLiquidoComposicao',
        nivel: 'cascata', permissao: 'custos'
    },

    {
        numero: '4.', titulo: 'CUSTO MATÉRIA PRIMA', tituloItem: 'Custo Matéria Prima', slug: 'custo-variavel',
        legenda: 'Custo Matéria Prima (custo variável) — ' + SOBRE_LIQUIDO,
        campoValor: 'custoVariavelTotal', campoPerc: 'percCustoVariavel',
        campoParticipacao: 'percCustoVariavel', campoComposicao: 'percCustoVariavelComposicao',
        nivel: 'cascata', permissao: 'custos'
    },
    {
        numero: '5. ( = )', titulo: 'MARGEM DE CONTRIBUIÇÃO', tituloItem: '( = ) Margem Contrib.', slug: 'margem-contribuicao',
        legenda: 'Margem de Contribuição — ' + SOBRE_LIQUIDO,
        campoValor: 'valorMargemContribuicao', campoPerc: 'percMargemContribuicao',
        campoParticipacao: 'percMargemContribuicao', campoComposicao: 'percMargemContribuicaoComposicao',
        nivel: 'cascata', permissao: 'custos'
    },
    {
        numero: '5.1. ( - )', titulo: 'CUSTO DIRETO', tituloItem: '( - ) Custo Direto', slug: 'custo-fixo',
        legenda: 'Custo Direto (custo fixo) — ' + SOBRE_LIQUIDO,
        campoValor: 'custoFixoTotal', campoPerc: 'percCustoFixo',
        campoParticipacao: 'percCustoFixo', campoComposicao: 'percCustoFixoComposicao',
        nivel: 'cascata', permissao: 'custos'
    },

    {
        numero: '5.2. ( - )', titulo: 'CUSTO INDIRETO', tituloItem: '( - ) Custo Indireto', slug: 'custo-indireto',
        legenda: 'Custo Indireto — marketing, despesas administrativas e outros',
        campoValor: 'valorCustoIndireto', campoPerc: 'percCustoIndireto',
        campoParticipacao: 'percCustoIndiretoParticipacao', campoComposicao: 'percCustoIndiretoComposicao',
        nivel: 'cascata', permissao: 'custos'
    },
    {
        titulo: 'Marketing', slug: 'marketing', legenda: 'Marketing — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorMarketing', campoPerc: 'percMarketing',
        campoParticipacao: 'percMarketingParticipacao', campoComposicao: 'percMarketingComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Despesas Administrativas', tituloItem: 'Desp. Adm.', slug: 'despesa-administrativa',
        legenda: 'Despesas Administrativas — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorDespesaAdministrativa', campoPerc: 'percDespesaAdministrativa',
        campoParticipacao: 'percDespesaAdministrativaParticipacao', campoComposicao: 'percDespesaAdministrativaComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'Outros', slug: 'outros', legenda: 'Outros — alíquota, ' + SOBRE_BRUTO,
        campoValor: 'valorOutros', campoPerc: 'percOutros',
        campoParticipacao: 'percOutrosParticipacao', campoComposicao: 'percOutrosComposicao',
        nivel: 'componente', permissao: 'custos'
    },

    {
        numero: '6. ( = )', titulo: 'RESULTADO LÍQUIDO', tituloItem: '( = ) Resultado Líquido', slug: 'resultado-liquido',
        legenda: 'Resultado Líquido — ' + SOBRE_LIQUIDO,
        campoValor: 'valorResultadoLiquidoTotal', campoPerc: 'percResultadoLiquidoTotal',
        campoParticipacao: 'percResultadoLiquidoTotal', campoComposicao: 'percResultadoLiquidoTotalComposicao',
        nivel: 'cascata', permissao: 'custos'
    },

    {
        numero: '7. ( - )', titulo: 'IR + CSLL', tituloItem: '( - ) IR + CSLL', slug: 'ir-csll',
        legenda: 'IR + CSLL — ' + SOBRE_LIQUIDO,
        campoValor: 'valorIrContribuicaoSocial', campoPerc: 'percIrContribuicaoSocial',
        campoParticipacao: 'percIrContribuicaoSocialParticipacao', campoComposicao: 'percIrContribuicaoSocialComposicao',
        nivel: 'cascata', permissao: 'custos'
    },
    {
        titulo: 'IR', slug: 'ir', legenda: 'Imposto de Renda — a alíquota incide sobre o resultado líquido, então difere da participação',
        campoValor: 'valorIr', campoPerc: 'percIr',
        campoParticipacao: 'percIrParticipacao', campoComposicao: 'percIrComposicao',
        nivel: 'componente', permissao: 'custos'
    },
    {
        titulo: 'CSLL', slug: 'csll', legenda: 'Contribuição Social — a alíquota incide sobre o resultado líquido, então difere da participação',
        campoValor: 'valorContribuicaoSocial', campoPerc: 'percContribuicaoSocial',
        campoParticipacao: 'percContribuicaoSocialParticipacao', campoComposicao: 'percContribuicaoSocialComposicao',
        nivel: 'componente', permissao: 'custos'
    },

    {
        numero: '8. ( = )', titulo: 'LUCRO LÍQUIDO', tituloItem: '( = ) Lucro Líquido', slug: 'lucro-liquido',
        legenda: 'Lucro Líquido (margem de lucro) — ' + SOBRE_LIQUIDO,
        campoValor: 'valorLucroLiquido', campoPerc: 'percLucroLiquido',
        campoParticipacao: 'percLucroLiquido', campoComposicao: 'percLucroLiquidoComposicao',
        nivel: 'cascata', permissao: 'drp'
    }
];

/**
 * Define se a tabela abre detalhada (cada componente do D.R.P em sua linha ou coluna) ou resumida
 * (apenas as etapas da cascata). Flag de avaliação: enquanto o time decide qual visão serve melhor
 * como padrão, basta trocar este valor.
 */
export const ABRIR_DETALHADO = false;

/** Se a etapa da cascata se abre em componentes — no modo detalhado da tabela de itens dá lugar a eles */
export function temComponentes(indice: number): boolean {
    return LINHAS_DRP[indice].nivel === 'cascata' && LINHAS_DRP[indice + 1]?.nivel === 'componente';
}
